import React, { useEffect, useMemo, useState } from "react";
import { ReplyEditor } from "@/components/ReviewReply";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@/types";
import { Star } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { ReviewItem, ReviewSummary } from "@/components/EventReviews";
import { Stars } from "@/components/SpotReviews";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

export default function VenueReviews() {
  const { t } = usePrefs();
  const owner = (useAuth().user as User).venue_role === "owner";
  const [s, setS] = useState<ReviewSummary | null>(null);
  useEffect(() => { api.get("/venue/event-reviews").then((r) => setS(r.data)).catch(() => setS({ avg: null, count: 0, items: [] })); }, []);
  const dist = useMemo(() => [5, 4, 3, 2, 1].map((n) => ({ n, c: s?.items.filter((r) => r.rating === n).length || 0 })), [s]);

  return (
    <div data-testid="venue-reviews-page" className="space-y-6">
      <PageTitle title={t("er.partnerTitle")} sub={t("er.partnerSub")} />
      {s && (
        <div className="rounded-3xl border bg-card p-5 flex flex-wrap items-center gap-6">
          <div className="text-center">
            <p data-testid="venue-reviews-avg" className="font-display text-5xl font-black">{s.avg ?? "—"}</p>
            <Stars value={Math.round(s.avg || 0)} />
            <p data-testid="venue-reviews-count" className="text-xs text-muted-foreground mt-1">{t("rev.count").replace("{n}", String(s.count))}</p>
          </div>
          <div className="flex-1 min-w-48 space-y-1.5">
            {dist.map((d) => (
              <div key={d.n} className="flex items-center gap-2 text-xs font-bold">
                <span className="w-6 inline-flex items-center gap-0.5">{d.n}<Star className="h-3 w-3 fill-amber-400 text-amber-400" /></span>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${s.count ? (d.c / s.count) * 100 : 0}%` }} /></div>
                <span className="w-6 text-muted-foreground">{d.c}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {s?.count === 0 && <p data-testid="venue-reviews-empty" className="text-center text-muted-foreground py-10">{t("er.partnerEmpty")}</p>}
      <div className="grid gap-3 md:grid-cols-2">{s?.items.map((r) => (
        <ReviewItem key={r.review_id} r={r} showEvent>
          {owner && <ReplyEditor r={r} onSaved={(x) => setS((p) => p && { ...p, items: p.items.map((i) => (i.review_id === x.review_id ? { ...i, reply: x.reply } : i)) })} />}
        </ReviewItem>
      ))}</div>
    </div>
  );
}
